import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Plus } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useConversations } from "../../hooks/useConversations";
import { formatRelativeTime } from "../../utils/conversation";
import { CreateConversationModal } from "./CreateConversationModal";
import type { ConversationData } from "../../types/conversation";

export function ConversationSwitcher() {
  const [open, setOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { conversations } = useConversations();
  const navigate = useNavigate();
  
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);
  
  const handleSelectConversation = (conversationId: string) => {
    navigate(`/pa/chat/${conversationId}`);
    setOpen(false);
  };
  
  const handleNewConversation = () => {
    setOpen(false);
    setShowCreateModal(true);
  };
  
  const handleConversationCreated = (conversation: ConversationData) => {
    navigate(`/pa/chat/${conversation.id}`);
  };
  
  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search conversations..." />
        <CommandList>
          <CommandEmpty>No conversations found.</CommandEmpty>
          <CommandGroup heading="Recent Conversations">
            {conversations.map((conversation) => (
              <CommandItem
                key={conversation.id}
                onSelect={() => handleSelectConversation(conversation.id)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <span>{conversation.title || "Untitled Conversation"}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(conversation.lastMessageAt || conversation.createdAt)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup>
            <CommandItem
              onSelect={handleNewConversation}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Conversation
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      
      <CreateConversationModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleConversationCreated}
      />
    </>
  );
}